<template>
  <div class="content-wrapper">
    <vue-final-modal v-model="showInfoModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showInfoModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <p>{{ information }}</p>
      </div>
    </vue-final-modal>

    <vue-final-modal v-model="showImageModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showImageModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <img v-bind:src="imageBase64" />
      </div>
    </vue-final-modal>

    <loading v-model:active="isLoading" :is-full-page="true" />

    <div class="assessment-header">
      <h2>Please answer the following screening questions:</h2>
    </div>

    <div class="simulated-card">
      <p class="simulated-title">Assessment Type:</p>
      <div class="simulated-dialog">
        <button v-on:click="isSimulated = false" :class="{ 'active': isSimulated === false }">
          <div class="button-content">
            <svg class="check-icon" width="24" height="24" viewBox="0 0 24 24">
              <path fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <span>Patient</span>
          </div>
        </button>
        <button v-on:click="isSimulated = true" :class="{ 'active': isSimulated === true }">
          <div class="button-content">
            <svg class="check-icon" width="24" height="24" viewBox="0 0 24 24">
              <path fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <span>Simulated</span>
          </div>
        </button>
      </div>
    </div>

    <div class="question-groups-container">
      <div class="question-group" v-for="(questionsInGroup, groupName) in groupedVisibleQuestions" v-bind:key="groupName">
        <h3 class="group-title" :class="{ 'rcat-title': isRCatSystem }">{{ groupName }}</h3>
        <div class="screening-questions__list">
          <div class="screening-question" v-for="question of questionsInGroup" v-bind:key="question.id">
            <div v-if="!isRCatSystem" class="question-header">
              <span>{{ question.title }} </span>

              <div class="question-actions">
                <button class="btn-icon" v-if="question.imageBase64 != null && question.imageBase64 != ''"
                  v-on:click="showImage(question.imageBase64)" title="View Image">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2.5L5.5 12 9 8.5V11h6V8.5l3.5 3.5-3.5 3.5z" />
                  </svg>
                </button>
                <button class="btn-icon" v-if="question.information != null && question.information != ''"
                  v-on:click="showInformation(question)" title="More Information">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="question-content">
              <p class="question-text">{{ question.question }}</p>

              <button class="response-button" v-on:click="toggleResponse(question)"
                :class="{ 'active': question.response === true }">
                <span>{{ question.response === true ? 'YES' : 'NO' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="action-buttons">
      <button class="btn-secondary" v-on:click="back()">
        <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
          <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
        </svg>
        Back
      </button>

      <button class="btn-primary" v-on:click="continueAssessment()">
        <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
          <path fill="currentColor" d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
        </svg>
        Continue Assessment
      </button>
    </div>

  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  data() {
    return {
      isLoading: false,
      questions: [],
      allQuestions: [], // Store all questions for reference

      information: "",
      imageBase64: "",

      showInfoModal: false,
      showImageModal: false,

      isSimulated: null,
      isRCatSystem: false,
    }
  },

  computed: {
    // Get questions that should be visible based on current responses
    visibleQuestions() {
      // Start with root questions (those without a related question)
      const rootQuestions = this.allQuestions.filter(q => !q.relatedQuestionId);

      // Add conditional questions based on responses
      const conditionalQuestions = this.allQuestions.filter(q => {
        if (!q.relatedQuestionId) return false;

        // Find the parent question
        const parentQuestion = this.allQuestions.find(parent => parent.id === q.relatedQuestionId);
        if (!parentQuestion) return false;

        // Check if parent question response matches the show condition
        if (q.relatedQuestionShowResponse === null) {
          // Show when parent has any response (not null/undefined)
          return parentQuestion.response !== null && parentQuestion.response !== undefined;
        } else if (q.relatedQuestionShowResponse === 1) {
          // Show when parent response is true
          return parentQuestion.response === true;
        } else if (q.relatedQuestionShowResponse === 0) {
          // Show when parent response is NOT true (false, null, or undefined)
          return parentQuestion.response !== true;
        }

        return false;
      });

      // Combine and sort by position
      const visible = [...rootQuestions, ...conditionalQuestions];
      return visible.sort((a, b) => a.position - b.position);
    },

    // Group visible questions by their group property
    groupedVisibleQuestions() {
      if (!this.visibleQuestions || this.visibleQuestions.length === 0) {
        return {};
      }

      // Get the order of groups from the original full question list to maintain a consistent order.
      const groupOrder = [...new Set(this.allQuestions.map(q => q.group ? q.group.trim() : 'Uncategorized'))];

      // Group the currently visible questions.
      const grouped = this.visibleQuestions.reduce((acc, question) => {
        const groupName = question.group ? question.group.trim() : 'Uncategorized';
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(question);
        return acc;
      }, {});

      // Create a new object with keys sorted according to the original groupOrder.
      const orderedGrouped = {};
      groupOrder.forEach(groupName => {
        // Only add the group if it has visible questions
        if (grouped[groupName]) {
          orderedGrouped[groupName] = grouped[groupName];
        }
      });

      return orderedGrouped;
    }
  },

  mounted() {
    this.isRCatSystem = localStorage.getItem('@app-system') === 'rcat';
    this.isLoading = true;
    this.axios.get("/api/screeningquestion").then((response) => {
      // Initialize all questions with default response = false
      this.allQuestions = response.data.map(question => ({
        ...question,
        response: false // Set default response to false
      }));

      this.questions = [...this.allQuestions]; // Keep for compatibility
      this.isLoading = false;

      // Check if we have local questions and if so we want to update our current question set
      var storedQuestionsJson = localStorage.getItem("@screening-questions");
      if (storedQuestionsJson != null) {
        this.updateFromStoredQuestions(JSON.parse(storedQuestionsJson));
      } else {
        // If no stored questions exist, save the initial state with default responses
        this.saveQuestions(this.allQuestions);
      }

      var isSimulated = localStorage.getItem("@is-simulated");
      this.isSimulated = isSimulated == null ? null : isSimulated == "true";
    })
  },


  methods: {
    back() {
      if (confirm("Are you sure you want to go back?")) {
        this.$router.push("/");
      }
    },

    updateFromStoredQuestions(storedQuestions) {
      for (let question of this.allQuestions) {
        var storedQuestion = storedQuestions.find(q => q.id == question.id);
        if (storedQuestion == null)
          continue;

        question.response = storedQuestion.response;
      }

      // Update the questions array for compatibility
      this.questions = [...this.allQuestions];

      // Save the updated questions back to localStorage
      this.saveQuestions(this.allQuestions);
    },

    toggleResponse(question) {
      if (question.response == null)
        question.response = false;

      question.response = !question.response;

      // Also update in allQuestions array
      const allQuestionsIndex = this.allQuestions.findIndex(q => q.id === question.id);
      if (allQuestionsIndex !== -1) {
        this.allQuestions[allQuestionsIndex].response = question.response;
      }

      // Update questions array for compatibility
      this.questions = [...this.allQuestions];

      this.saveQuestions(this.allQuestions);

      // Force reactivity update for computed property
      this.$forceUpdate();
    },

    showInformation(question) {
      this.information = question.information;
      this.showInfoModal = true;
    },

    showImage(imageBase64) {
      this.imageBase64 = imageBase64;
      this.showImageModal = true;
    },

    saveQuestions(questions) {
      localStorage.setItem("@screening-questions", JSON.stringify(questions));
    },

    continueAssessment() {
      if (this.isSimulated == null) {
        alert("Please select if this is a patient or a test");
        return;
      }

      // Save the current state of questions before navigating
      this.saveQuestions(this.allQuestions);

      this.$router.push("/survey");
    },
  },

  watch: {
    // whenever question changes, this function will run
    isSimulated(isSim) {
      localStorage.setItem("@is-simulated", isSim);
    }
  },

  components: {
    Loading
  },
}
</script>

<style lang="scss" scoped>
@import "../../breakpoints.scss";

.content-wrapper {
  margin: 0 auto;
  padding: 20px 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.assessment-header {
  margin-bottom: 24px;

  h2 {
    font-size: 22px;
    font-weight: 600;
    color: #111827;
    margin: 0;

    @include md {
      font-size: 24px;
    }
  }
}

.simulated-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #E5E7EB;

  .simulated-title {
    font-weight: 600;
    color: #111827;
    margin: 0 0 12px 0;
    font-size: 16px;
  }
}

.simulated-dialog {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  button {
    background: white;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 60px;
    overflow: hidden;

    &:hover {
      border-color: #9CA3AF;
      background-color: #F9FAFB;
    }

    &.active {
      border-color: #013B82;
      background-color: rgba(1, 59, 130, 0.05);

      .check-icon {
        color: #013B82;
      }

      span {
        color: #013B82;
        font-weight: 600;
      }
    }

    .button-content {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;

      @include md {
        justify-content: flex-start;
        padding: 0 20px;
      }

      .check-icon {
        color: #D1D5DB;
        margin-right: 8px;
        flex-shrink: 0;
      }

      span {
        font-family: 'Source Sans Pro', sans-serif;
        font-size: 16px;
        color: #000001;

        @include md {
          font-size: 16px;
        }
      }
    }
  }
}

.question-groups-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 30px;
}

.group-title {
  font-size: 20px;
  font-weight: 600;
  color: #1F2937;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #013B82;
  
  &.rcat-title {
    background: linear-gradient(135deg, #013B82, #0A2D5C);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    border-bottom: none;
    margin-bottom: 20px;
  }
}

.screening-questions__list {
  display: grid;
  gap: 16px;

  @include md {
    grid-template-columns: 1fr 1fr;
  }
}

.screening-question {
  background: white;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
    transform: translateY(-1px);
  }
}

.question-header {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  padding: 12px 16px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;

  span {
    flex: 1;
    text-align: left;
  }

  .question-actions {
    display: flex;
    gap: 8px;
  }

  .btn-icon {
    background: transparent;
    border: none;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.2s;
    padding: 0;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
}

.question-content {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .question-text {
    flex: 1;
    margin: 0;
    color: #000001;
    font-size: 15px;
    line-height: 1.5;
  }

  .response-button {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #F3F4F6;
    border: 2px solid #D1D5DB;
    color: #000001;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      background: #E5E7EB;
      border-color: #9CA3AF;
    }

    &.active {
      background: #013B82;
      border-color: #013B82;
      color: white;
      box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
    }
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;

  @include md {
    flex-direction: row;
    justify-content: space-between;
  }
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: 100%;

  @include md {
    width: auto;
  }

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #000001;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}

::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 24px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: white;
  width: 90%;
  max-width: 800px;
  overflow-y: auto;
  max-height: 90vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 4px;

    @include md {
      width: auto;
      max-width: 100%;
      max-height: 70vh;
    }
  }

  p {
    line-height: 1.6;
    color: #000001;
    font-size: 16px;
  }
}

.modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 32px;
  color: #6B7280;
  cursor: pointer;
  transition: color 0.2s;


  &:hover {
    color: #111827;
  }
}

.modal__content {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}
</style>